import classNames from 'classnames';
import { useEffect, useMemo } from 'react';

import { PaymentInstrumentStripe, SubscriptionDetailsv4, SubscriptionPackage } from '@bpm-web-app/supreme-api-sdk';
import { useGetMeSubscription, useGetPaymentInstrument } from '@bpm-web-app/swr-hooks';
import { formatDateToString } from '@bpm-web-app/utils';
import Link from 'next/link';
import IconPayment from '../../components/icons/payment.svg';
import accountStyles from '../account-overview.module.css';
import styles from './your-plan.module.css';
import { Button } from '../../components/button/button';
import { StatusNote } from './status-note';
import { SubscriptionStatus } from './subscription-status';
import { ResumeButton } from './resume-button/resume-button';
import { usePlatform } from '../../helpers/usePlatform';
import { ResubscribeButton } from './resume-button/resubscribe-button';

const options = {
    [SubscriptionPackage.LibraryEnum.Music]: 'Open Format',
    [SubscriptionPackage.LibraryEnum.Latin]: 'Latino + Global',
};

export const YourPlan = () => {
    const platform = usePlatform();
    const { data: userSubscription } = useGetMeSubscription(platform);
    const { data: paymentInstrument } = useGetPaymentInstrument(platform);

    const { style, membership, subscription } = useMemo(() => {
        const userMembership = userSubscription?.data.membership;
        const subscr = userMembership?.subscription;
        const subPkg = subscr?.package;

        if (subPkg) {
            const theme = (subPkg?.library === SubscriptionPackage.LibraryEnum.All
                ? 'all'
                : subPkg?.name ?? '')
                .toString().toLowerCase();

            return {
                style: {
                    '--color-subscription': `var(--color-subscription-${(theme)})`
                } as React.CSSProperties,
                subscription: subscr,
                membership: userMembership
            };
        }
        return { style: {} as React.CSSProperties, subscription: null, membership: userMembership };
    }, [userSubscription]);

    /** useEffect(() => {
        (window as any).ChurnBuster = (window as any).ChurnBuster || {};
        if (userSubscription && userSubscription.data.membership.has_membership && !userSubscription.data.membership.is_paused) {
            if (userSubscription.data.membership.subscription?.method === SubscriptionDetailsv4.MethodEnum.Paypal) {
                (window as any).ChurnBuster.cancelAttributes = {
                    accountId: '2517eff3-2fb6-4027-be2b-2690b264f484',
                    source: 'paypal',
                    customerId: userSubscription.data.membership.subscription.external_id,
                    customerEmail: userSubscription.data.user.email,
                    cancelButtonSelector: '#cancel-button',
                    customProperties: {
                        platform: userSubscription.data.membership.subscription.platform
                    }
                };
                const script = document.createElement('script');
                script.type = 'text/javascript';
                script.async = true;
                script.src = 'https://proxy-assets.churnbuster.io/v4/scripts.js?v=1678742553659';
                const insert = document.getElementsByTagName('script')[0];
                insert.parentNode?.insertBefore(script, insert);
            } else if (userSubscription.data.membership.subscription?.method === SubscriptionDetailsv4.MethodEnum.Stripe) {
                (window as any).ChurnBuster.cancelAttributes = {
                    accountId: '2517eff3-2fb6-4027-be2b-2690b264f484',
                    source: 'stripe',
                    customerId: userSubscription.data.membership.subscription.external_id,
                    customerEmail: userSubscription.data.user.email,
                    cancelButtonSelector: '#cancel-button',
                    customProperties: {
                        platform: userSubscription.data.membership.subscription.platform
                    }
                };
                const script = document.createElement('script');
                script.type = 'text/javascript';
                script.async = true;
                script.src = 'https://proxy-assets.churnbuster.io/v4/scripts.js?v=1678742553659';
                const insert = document.getElementsByTagName('script')[0];
                insert.parentNode?.insertBefore(script, insert);
            }
        }
    }, [userSubscription]);* */

    const creditAmount = platform === 'create' ? userSubscription?.data.user.stripe_create_balance : userSubscription?.data.user.stripe_supreme_balance;

    return (
        <section
            {...{ style }}
            className={classNames(accountStyles['account__section'], styles['plan-container'])}
        >
            <div className={styles['plan__heading']}>
                <div>
                    <h2 className={classNames(accountStyles['account__section-title'], styles['plan__title'])}>Your plan</h2>
                    <p className={classNames(accountStyles['account__section-title'], styles['plan__plan-title'])}>
                        <span>
                            {subscription?.is_supreme_bundle ? 'All Access' : subscription?.package?.display_title || subscription?.package?.name || 'Free Plan'}
                        </span> {subscription?.library === SubscriptionPackage.LibraryEnum.All ? '' : !!subscription?.library && options[subscription?.library]}
                    </p>
                </div>

                <div className={styles['plan__status-wrapper']}>
                    <SubscriptionStatus membership={membership} status={subscription?.status} />
                    <StatusNote membership={membership} />
                </div>
            </div>

            <div className={styles['plan__footer']}>
                <div>
                    <p className={styles['plan__price']}>
                        ${membership?.is_supreme_bundle ? '0.00' : membership?.is_one_time && membership.discounted ? membership.discounted.amount : subscription?.package?.amount ?? '0.00'}/
                        {subscription?.renewal_interval === 1 || !membership?.has_membership ? 'mo' : 'yr'}
                        {!!membership?.discounted && '*'}
                    </p>

                    {creditAmount !== '0.00' && (
                        <p className={styles['plan__credit']}>
                            ${creditAmount} Remaining Credit
                        </p>
                    )}

                    {!!subscription?.next_billing && (
                        <ul className={styles['plan__meta']}>
                            {membership?.subscription?.status !== SubscriptionDetailsv4.StatusEnum.Cancelled &&
                                (
                                    <li>
                                        {membership?.is_supreme_bundle ? 'Included with your BPM Supreme Subscription' : `Next payment ${formatDateToString(subscription.next_billing, 'L')}`}
                                    </li>
                                )
                            }
                            {!!paymentInstrument && (
                                <li>
                                    <IconPayment /> {(paymentInstrument?.data as PaymentInstrumentStripe).description}
                                </li>
                            )}
                        </ul>
                    )}
                </div>

                {membership?.has_membership ? (
                    <div className={styles['plan__actions']}>
                        {membership.subscription?.status === SubscriptionDetailsv4.StatusEnum.Cancelled && !membership.subscription.next_cycle_package_change ? (
                            <ResubscribeButton membership={membership} />
                        ) : (
                            <>
                                {!membership.is_supreme_bundle && (
                                    <Link href={`./plan/cancel/${subscription?.package_id}?platform=${platform}`} passHref>
                                        <Button data-subscription-id={userSubscription?.data.membership.subscription?.external_id} id="cancel-button" type="button" variant="secondary" className={styles['plan__button']}>
                                            Cancel
                                        </Button>
                                    </Link>
                                )}
                                {membership.is_paused || membership.paused_until ? (
                                    <ResumeButton
                                        paymentInstrument={paymentInstrument?.data}
                                        membership={membership}
                                    />
                                ) : membership.is_one_time ?
                                    <Link href="/account/suuport" passHref>
                                        <Button as="a" className={styles['plan__button']}>
                                            Contact Support
                                        </Button>
                                    </Link> : (
                                        membership.is_supreme_bundle ? (
                                            // eslint-disable-next-line @typescript-eslint/dot-notation
                                            <Link href={`${process.env['NEXT_PUBLIC_SUPREME_PLATFORM_URL']}d/account/plan`} passHref>
                                                <Button as="a" className={styles['plan__button']}>
                                                    Manage on BPM Supreme
                                                </Button>
                                            </Link>
                                        ) : (
                                            <Link href="./plan" passHref>
                                                <Button as="a" className={styles['plan__button']}>
                                                    Change Plan
                                                </Button>
                                            </Link>
                                        )
                                    )}
                            </>
                        )}
                    </div>
                ) : (
                    <div className={styles['plan__actions']}>
                        <Link href="./plan" passHref>
                            <Button as="a" className={styles['plan__button']}>
                                See Plans
                            </Button>
                        </Link>
                    </div>
                )}
            </div>
        </section>
    );
};
